import Box from "@mui/material/Box";
import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import BackendPaginationTable from "common/Table/TableWithPagination";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import {
  useAssignBusinessInvoice,
  useGetBusinessInvoiceService,
  useUnassignBusinessInvoice,
} from "subModule/src/services/accountsettings/useAccountSettings";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DeleteData } from "subModule/src/type";
import { InvoiceTemplate } from "subModule/src/services/invoice/interface";
import FormDialog from "common/Modal/Modal";
import { Grid, SelectChangeEvent, Typography } from "@mui/material";
import { InvoiceProfileService } from "subModule/src/core/services";
import DescriptionIcon from "@mui/icons-material/Description";
import InvoiceTemplatesSearchHeader from "components/AccountSettings/InvoiceTemplatesSearchHeader";

interface SearchFormData {
  [key: string]: string | any;
}

export default function InvoiceTemplatesListing() {
  const navigate = useNavigate();
  const loggedInUserData = useSelector(selectUserInfo);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(false);
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [row, setRow] = useState<InvoiceTemplate>({});
  const [filter, setFilter] = useState<string[]>(["name", "type"]);
  const [search, setSearch] = useState<Record<string, string | number>>({});
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    name: "",
    type: 0,
    typeTitle: "",
  });
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetBusinessInvoiceService(
    { pageNumber: pageNo, size: pageSize, ...search },
    loggedInUserData?.business?.id,
  );
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  let allInvoicesAssigned: unknown = tableData?.pages[0].data?.content
    .map((item) => item.linked)
    .every((linked) => linked === true);
  const fetchData = useCallback(() => {
    refetch().then((res: any) => {
      if (!res?.data?.pages[0]?.success)
        enqueueSnackbar(
          <AlertMessages
            text={res?.data?.pages[0]?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
    });
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search]);
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const clearSearch = () => {
    setFilter(["name", "type"]);
    setSearchFormData({ name: "", type: 0, typeTitle: "" });
    setSearch({});
    setPageNo(1);
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    const selectedFilters =
      typeof value === "string" ? value.split(",") : value;
    setFilter(selectedFilters);
    setSearchFormData((prevFormData) => {
      const updatedFormData = Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          selectedFilters.includes(key)
            ? prevFormData[key]
            : typeof prevFormData[key] === "string"
              ? ""
              : 0,
        ]),
      );
      if (selectedFilters.includes("type") && prevFormData.typeTitle) {
        updatedFormData.typeTitle = prevFormData.typeTitle;
      }
      return updatedFormData;
    });
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val =
      field === "type" ? (value ? parseInt(value.value) || 0 : 0) : value;
    setSearchFormData((prevData) => ({
      ...prevData,
      [field]: val,
      ...(field === "type" && value?.label ? { typeTitle: value.label } : {}),
    }));
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string | number> = {};
    Object.entries(searchFormData).forEach(([key, value]) => {
      if (key === "typeTitle") return;
      if (value) {
        filteredSearch[key] = typeof value === "string" ? value?.trim() : value;
      }
    });
    setSearch(filteredSearch);
    setPageNo(1);
  };
  const assignUnassignSingleEntity = (isChecked: boolean, row: any) => {
    setLoading(true);
    if (isChecked) {
      onAssignService({
        businessId: loggedInUserData?.business?.id,
        templateId: row.id,
      });
    } else {
      onUnassignService({
        businessId: loggedInUserData?.business?.id,
        templateId: row.id,
      });
    }
  };
  const { onAssignService } = useAssignBusinessInvoice({
    onSuccess: (msg) => {
      const response: any = {};
      Object.keys(msg).forEach((key, index) => {
        response[key] = Object.values(msg)[index];
      });
      if (response.success) {
        if (
          tableData?.pages[0].data.content.length ===
          tableData?.pages[0].data.content.filter((invoice) => invoice.linked)
            .length
        ) {
          allInvoicesAssigned = true;
        }
        refetch();
        setLoading(false);
        enqueueSnackbar(
          <AlertMessages
            statusCode={response.status}
            text={t("template") + " " + t("assigned") + " " + t("successfully")}
          />,
          { variant: "success" },
        );
      } else {
        refetch();
        setLoading(false);
        enqueueSnackbar(<AlertMessages statusCode={response.status} />, {
          variant: "error",
        });
      }
    },
    onError: (err: any) => {
      enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      if (err instanceof Error) {
        console.error(err.stack);
      }
    },
  });
  const { onUnassignService } = useUnassignBusinessInvoice({
    onSuccess: (data) => {
      const response: any = {};
      Object.keys(data).forEach((key, index) => {
        response[key] = Object.values(data)[index];
      });
      if (response.success) {
        if (
          tableData?.pages[0].data.content.length !==
          tableData?.pages[0].data.content.filter((invoice) => invoice.linked)
            .length
        ) {
          allInvoicesAssigned = false;
        }
        refetch();
        setLoading(false);
        enqueueSnackbar(
          <AlertMessages
            statusCode={response.status}
            text={
              t("template") + " " + t("unassigned") + " " + t("successfully")
            }
          />,
          { variant: "success" },
        );
      } else {
        refetch();
        setLoading(false);
        enqueueSnackbar(
          <AlertMessages
            statusCode={response.status}
            text={response.message}
          />,
          {
            variant: "error",
          },
        );
      }
    },
    onError: (err: any) => {
      console.log("err====,  ", err);
      enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
    },
  });
  const onEdit = (row: any) => {
    if (row.default === false) navigate("/business-template/" + row.id);
  };
  const showRowView = (row: any) => {
    navigate("/business-template/" + row.id, {
      state: { isViewMode: true },
    });
  };
  const onDelete = (row: any) => {
    if (row.default === false) {
      setDeleteModal(true);
      setRow(row);
    }
  };
  const [actionsList, setActionsList] = useState<any[]>([
    { label: "view", onClick: showRowView, icon: <VisibilityIcon /> },
    { label: "edit", onClick: onEdit, icon: <EditIcon /> },
    { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
  ]);
  const menuOpen = (currentRow: any) => {
    if (currentRow.default === true) {
      setActionsList([
        { label: "view", onClick: showRowView, icon: <VisibilityIcon /> },
      ]);
    } else {
      setActionsList([
        { label: "view", onClick: showRowView, icon: <VisibilityIcon /> },
        { label: "edit", onClick: onEdit, icon: <EditIcon /> },
        { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
      ]);
    }
  };
  const adjustActions = (actions: any[]) => {
    return checkPrivileges("ROLE_INVOICE_TEMPLATE_UPDATE")
      ? checkPrivileges("ROLE_INVOICE_TEMPLATE_ARCHIVE")
        ? actions
        : actions.filter((action) => action.label !== "delete")
      : checkPrivileges("ROLE_INVOICE_TEMPLATE_ARCHIVE")
        ? actions.filter((action) => action.label !== "edit")
        : [];
  };
  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/business-template/create")}
        heading={t("businessTemplates")}
        icon={
          <DescriptionIcon
            sx={{
              fill: "#000",
              width: "30px",
              height: "30px",
              marginTop: "3px",
            }}
          />
        }
        btnText={"create"}
        btnType={"create"}
        showCreateBtn={checkPrivileges("ROLE_INVOICE_TEMPLATE_CREATE")}
      />
      <Box>
        <Box className="w-[100%] ">
          <BackendPaginationTable
            columns={[
              {
                id: "invoiceTemplateName",
                label: "name",
                numeric: false,
                width: "28%",
              },
              {
                id: "businesstemplateType",
                label: "type",
                numeric: false,
                width: "20%",
              },
              {
                id: "designVariant",
                label: "designVariant",
                numeric: false,
                width: "24%",
              },
              {
                id: "default",
                label: "default",
                numeric: false,
                width: "16%",
              },
              ...(adjustActions(actionsList).length
                ? [
                    {
                      id: "actions",
                      label: "actions",
                      numeric: false,
                      width: "8%",
                    },
                  ]
                : []),
            ]}
            data={tableData?.pages[0]?.data?.content || []}
            pageNo={pageNo}
            pageSize={pageSize}
            tableData={tableData?.pages[0]?.data}
            showCheckbox={true}
            checkBoxAll={"checkBoxAll"}
            showActions={true}
            enableSorting={true}
            showHeader={true}
            showSearch={true}
            showFilter={true}
            showCreateButton={false}
            showRowView={showRowView}
            rowClick={checkPrivileges("ROLE_INVOICE_TEMPLATE_UPDATE")}
            actions={adjustActions(actionsList)}
            handleChangePage={handleChangePage}
            handleChangeRows={handleChangeRows}
            assignUnassignSingleEntity={assignUnassignSingleEntity}
            allEntitiesAssigned={allInvoicesAssigned}
            loadingSearch={isFetching || loading}
            assignPrivilege={true}
            unassignPrivilege={true}
            displayDefault={true}
            menuOpen={menuOpen}
            headerComponent={
              <InvoiceTemplatesSearchHeader
                searchFormData={searchFormData}
                handleChangeFilter={handleChangeFilter}
                handleChangeSearch={handleChangeSearch}
                handleSubmitSearch={handleSubmitSearch}
                clearSearch={clearSearch}
                {...{ filter, search }}
              />
            }
          />
        </Box>
        {openDeleteModal && (
          <FormDialog
            open={openDeleteModal}
            onClose={() => setDeleteModal(false)}
            title={t("delete") + " " + t("template")}
            content={
              <Grid container>
                <Grid item xs={12}>
                  <Typography>
                    {t("Are you sure you want to delete?")}
                  </Typography>
                </Grid>
              </Grid>
            }
            saveFunction={() => {
              setDeleteModal(false);
              setLoading(true);
              InvoiceProfileService.deleteInvoiceTemplate((row as any).id)
                .then((response: DeleteData) => {
                  if (response.success) {
                    refetch();
                    if (tableData?.pages[0].data.content.length === 1)
                      handleChangePage(pageNo - 1);
                    enqueueSnackbar(
                      <AlertMessages
                        statusCode={response.status}
                        text={
                          t("template") +
                          " " +
                          t("deleted") +
                          " " +
                          t("successfully")
                        }
                      />,
                      { variant: "success" },
                    );
                  } else {
                    enqueueSnackbar(
                      <AlertMessages statusCode={response.status} />,
                      { variant: "error" },
                    );
                  }
                })
                .catch((err: any) => {
                  console.error("===", err);
                  enqueueSnackbar(
                    <AlertMessages
                      text={err?.data?.message || err || "Something went wrong"}
                    />,
                    { variant: "error" },
                  );
                })
                .finally(() => setLoading(false));
            }}
            submitButtonText="Delete"
          />
        )}
      </Box>
    </Box>
  );
}
